import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  Rocket,
  Compass,
  Handshake,
  Cpu,
  Globe2,
  FlaskConical,
  Award,
  Target,
  Zap,
  Users as UsersIcon,
} from "lucide-react";
import { TEAM } from "@/data/team";
import Seo from "@/components/seo/Seo";
import { getPublishedCaseStudies } from "@/data/caseStudies";
import CommercialCTA from "@/components/common/CommercialCTA";

// ---- Journey timeline (from cloudlabweb.com/about-us) ----
const TIMELINE = [
  {
    year: "2012",
    title: "An exciting journey begins",
    desc: "Delivered our first design project and started building Acadmax — our first flagship software product for campus management.",
    icon: Rocket,
  },
  {
    year: "2014",
    title: "New dimensions",
    desc: "Rode the wave of educational digitalisation and made real headway in the e-learning industry with Acadmax's first release.",
    icon: Compass,
  },
  {
    year: "2015",
    title: "Partnering for mutual growth",
    desc: "Started shaking hands with international sales partners, launched back-office support services and expanded to a 24 × 5 delivery model.",
    icon: Handshake,
  },
  {
    year: "2019",
    title: "Focused on AI",
    desc: "Upscaled our technology stack, won a national award and worked with the Pakistan Science Foundation on a large nationwide project.",
    icon: Cpu,
  },
  {
    year: "2020",
    title: "Expansion & consolidation",
    desc: "Expanded operations and partnerships and opened our operational office in Islamabad.",
    icon: Globe2,
  },
  {
    year: "Today",
    title: "Research & development",
    desc: "Learning, prototyping and shipping new AI-first technologies to the industry — from AI concierges to admissions co-pilots.",
    icon: FlaskConical,
  },
];

// ---- Values ----
const VALUES = [
  { icon: Sparkles, title: "Craft over quantity", desc: "Beautifully-engineered products, always. If it's not our best, it doesn't ship." },
  { icon: Target,   title: "Outcomes, not slideware", desc: "Every engagement is measured against a real business metric — never activity." },
  { icon: Zap,      title: "Bias for shipping", desc: "We prototype in weeks, not quarters. Learning happens in production." },
  { icon: UsersIcon,title: "People-first",     desc: "Senior teams, respectful hours and a culture where great engineers stay." },
];

// ---- Stats strip ----
const STATS = [
  { v: "500+",  l: "Projects delivered" },
  { v: "12+",   l: "Years shipping software" },
  { v: "6",     l: "Countries served" },
  { v: "60+",   l: "Enterprise clients" },
];

export default function About() {
  return (
    <div data-testid="about-page" className="relative">
      <Seo
        title="About CloudLab Technologies"
        description="Since 2012, CloudLab has built an award-winning team of engineers, designers and AI specialists shipping enterprise software across the UK, USA, Germany, KSA, UAE and Pakistan."
        keywords="about CloudLab, CloudLab Technologies, software house Islamabad, AI company UK, engineering team Pakistan"
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(1100px 500px at 15% 0%, hsl(var(--primary) / 0.12) 0%, transparent 60%), radial-gradient(900px 400px at 100% 20%, hsl(var(--secondary) / 0.10) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 pt-16 md:pt-24 pb-10 md:pb-16">
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-border/60 bg-background/70 backdrop-blur-sm">
            <Sparkles className="w-3.5 h-3.5 text-primary" />
            <span className="text-[11px] font-mono uppercase tracking-[0.22em]">Our Story</span>
          </div>

          <div className="mt-6 grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <h1 className="font-heading font-black tracking-tight leading-[0.98] text-5xl md:text-6xl lg:text-[5.5rem]">
                Bringing quality solutions{" "}
                <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
                  to our clients.
                </span>
              </h1>
              <p className="mt-6 text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed">
                Since 2012, CloudLab has systematically built a team of highly-qualified consultants and engineers with
                an enviable reputation for delivering quality services at every level. Excellent academic credentials
                combined with practical, real-world experience — and a systems-approach to problem-solving that lets us
                think laterally and creatively for our clients.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/quote" data-testid="about-cta-primary">
                  <button className="group rounded-full pl-6 pr-2 py-2 inline-flex items-center gap-3 text-sm font-medium bg-primary text-primary-foreground shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]">
                    <span>Start a project</span>
                    <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>
                </Link>
                <Link to="/contact" data-testid="about-cta-secondary">
                  <button className="rounded-full px-6 py-3 border border-border text-sm font-medium hover:bg-foreground/5 transition-colors">
                    Meet the team
                  </button>
                </Link>
              </div>
            </div>

            {/* Vertical stats card */}
            <div className="lg:col-span-4">
              <div className="rounded-3xl border border-border bg-card p-6">
                <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-primary">By the numbers</div>
                <div className="mt-4 grid grid-cols-2 gap-6">
                  {STATS.map((s) => (
                    <div key={s.l}>
                      <div className="font-heading font-black text-3xl md:text-4xl text-primary leading-none">{s.v}</div>
                      <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.22em] text-muted-foreground leading-snug">{s.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY TIMELINE — horizontal */}
      <section className="py-14 md:py-20 border-t border-border/60 bg-muted/30 overflow-hidden" data-testid="about-journey">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">The journey</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              12 years of shipping. One relentless arc.
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
              From a small design studio delivering our first project in 2012 to an AI-first product company
              operating across six countries — here&apos;s how the story unfolded.
            </p>
          </div>

          <div className="relative mt-14">
            {/* Horizontal rail */}
            <div
              aria-hidden="true"
              className="hidden md:block absolute left-0 right-0 top-6 h-px bg-gradient-to-r from-primary/40 via-border to-primary/20"
            />
            {/* Vertical rail (mobile) */}
            <div
              aria-hidden="true"
              className="md:hidden absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-primary/40 via-border to-primary/20"
            />

            <ol className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-4 relative">
              {TIMELINE.map((t, i) => {
                const Icon = t.icon;
                return (
                  <motion.li
                    key={t.year}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.4, delay: 0.06 * i }}
                    className="relative md:pt-16 pl-20 md:pl-0"
                    data-testid={`journey-step-${i}`}
                  >
                    {/* Node dot on the rail */}
                    <div className="absolute md:top-0 md:left-1/2 md:-translate-x-1/2 left-6 top-0 -translate-x-1/2 md:translate-x-[-50%]">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]">
                          <Icon className="w-5 h-5" strokeWidth={1.8} />
                        </div>
                        <span
                          aria-hidden="true"
                          className="absolute inset-0 rounded-2xl border border-primary/40 animate-ping"
                          style={{ animationDuration: "3.5s" }}
                        />
                      </div>
                    </div>

                    {/* Card */}
                    <div className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 hover:shadow-[0_20px_50px_-20px_hsl(var(--primary)/0.25)] transition-all h-full">
                      <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-primary">
                        {t.year}
                      </div>
                      <div className="mt-2 font-heading font-bold text-base md:text-lg leading-tight">
                        {t.title}
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {t.desc}
                      </p>
                    </div>
                  </motion.li>
                );
              })}
            </ol>
          </div>
        </div>
      </section>

      {/* MANAGEMENT TEAM */}
      <section className="py-14 md:py-20" data-testid="about-team">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between gap-6 max-w-4xl">
            <div>
              <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Leadership</div>
              <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Meet the team behind CloudLab.
              </h2>
              <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
                Small enough to move fast, senior enough to make it work. Every leader here has shipped
                products used by hundreds of thousands of people.
              </p>
            </div>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {TEAM.map((m, i) => (
              <motion.article
                key={m.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                data-testid={`team-${m.name.replace(/\s+/g, "-").toLowerCase()}`}
                className="group rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/40 hover:shadow-[0_25px_60px_-20px_hsl(var(--primary)/0.25)] transition-all"
              >
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={m.photo}
                    alt={m.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0) 55%, rgba(15,23,42,0.55) 80%, rgba(15,23,42,0.95) 100%)",
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-4">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary-foreground/80">
                      {m.role}
                    </div>
                    <div className="font-heading font-bold text-lg text-white leading-tight mt-0.5">
                      {m.name}
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.bio}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM COLLAGE */}
      <section className="py-14 md:py-20 relative overflow-hidden" data-testid="about-team-collage">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">The bigger picture</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Team{" "}
              <span className="italic underline decoration-[3px] underline-offset-[6px] text-primary decoration-primary">
                Collage
              </span>
            </h2>
            <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
              Behind every product line and every client relationship — the engineers, designers, analysts
              and support crew who make CloudLab, CloudLab.
            </p>
          </div>

          {/* Creative frame */}
          <div className="mt-10 md:mt-14 relative">
            {/* soft glow behind */}
            <div
              aria-hidden="true"
              className="absolute -inset-6 md:-inset-10 rounded-[36px] blur-3xl opacity-40"
              style={{
                background:
                  "radial-gradient(60% 60% at 30% 40%, hsl(var(--primary) / 0.35) 0%, transparent 70%), radial-gradient(50% 50% at 80% 60%, hsl(var(--secondary) / 0.25) 0%, transparent 70%)",
              }}
            />

            {/* gradient border wrapper */}
            <div
              className="relative rounded-[28px] p-[2px]"
              style={{
                background:
                  "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--secondary)) 45%, hsl(var(--primary) / 0.4) 100%)",
              }}
            >
              <div className="rounded-[26px] overflow-hidden bg-card">
                <div className="relative">
                  <img
                    src="https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/84t2a77r_ChatGPT%20Image%20Jul%2026%2C%202026%2C%2011_49_23%20PM_compressed.webp"
                    alt="CloudLab Technologies — team collage"
                    className="block w-full h-auto object-cover"
                    loading="lazy"
                    data-testid="team-collage-image"
                  />
                  {/* subtle bottom gradient for legibility of the caption */}
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.7) 100%)",
                    }}
                  />
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 flex items-end justify-between gap-4">
                    <div className="text-white">
                      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary-foreground/85">
                        CloudLab · Team of 30+ specialists
                      </div>
                      <div className="mt-0.5 font-heading font-bold text-lg md:text-xl leading-tight">
                        Engineering · Design · AI · Support · Operations
                      </div>
                    </div>
                    <div className="hidden md:flex items-center gap-2 text-white/90 text-xs font-mono uppercase tracking-[0.22em]">
                      <span>2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* decorative corner marks */}
            <CornerMark className="absolute -top-3 -left-3" />
            <CornerMark className="absolute -top-3 -right-3 rotate-90" />
            <CornerMark className="absolute -bottom-3 -left-3 -rotate-90" />
            <CornerMark className="absolute -bottom-3 -right-3 rotate-180" />
          </div>
        </div>
      </section>
      {/* VALUES */}
      <section className="py-14 md:py-20 bg-muted/30 border-y border-border/60">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">What we believe</div>
            <h2 className="mt-2 font-heading font-bold text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Four principles we ship by.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={v.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="rounded-2xl border border-border bg-card p-6"
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.7} />
                  </div>
                  <div className="mt-4 font-heading font-bold text-lg leading-tight">{v.title}</div>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AWARDS / RECOGNITION strip */}
      <section className="py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-border bg-card p-6 md:p-8 flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                <Award className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-primary">Recognition</div>
                <div className="font-heading font-bold text-lg leading-tight">
                  National award winners · Pakistan Science Foundation partner
                </div>
              </div>
            </div>
            <div className="text-sm text-muted-foreground max-w-lg">
              Trusted by the Government of Pakistan, Wah Engineering College, National Defence University,
              International Islamic University, HITEC and QAU — alongside global names like Toyota, Pizza Hut,
              Burger King, ICI and NHA.
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative rounded-[32px] overflow-hidden p-8 md:p-14 bg-foreground text-background shadow-2xl">
            <div
              aria-hidden="true"
              className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full opacity-40 blur-3xl pointer-events-none bg-primary"
            />
            <div className="relative grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="text-xs font-mono uppercase tracking-[0.25em] text-primary">Say hello</div>
                <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight">
                  Let&apos;s build something people talk about.
                </h2>
                <p className="mt-4 text-background/80 leading-relaxed max-w-lg">
                  Whether you have a full spec or just a rough idea — we&apos;ll come back within
                  one business day with a realistic scope, timeline and price.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link to="/quote" data-testid="about-final-cta-primary">
                  <button className="group inline-flex items-center gap-3 rounded-full pl-6 pr-2 py-2 text-sm font-medium bg-primary text-primary-foreground">
                    <span>Start a project</span>
                    <span className="w-8 h-8 rounded-full bg-background text-primary flex items-center justify-center transition-transform group-hover:translate-x-0.5">
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </button>
                </Link>
                <Link to="/contact" data-testid="about-final-cta-secondary">
                  <button className="rounded-full px-6 py-3 border border-background/30 text-sm font-medium hover:bg-background/10 transition-colors">
                    Talk to sales
                  </button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Phase 3.3 — Verified trust band (data-driven, small, additive).
          Reads published case studies + real service catalogue; no
          fabricated logos, awards or client counts. Ends with a lower-
          friction "Discuss Your Project" CTA. */}
      <AboutTrustBand />
    </div>
  );
}

function AboutTrustBand() {
  const caseStudies = getPublishedCaseStudies();
  const services = [
    { slug: "ai-development", name: "AI Development" },
    { slug: "ai-agent-development", name: "AI Agent Development" },
    { slug: "custom-software-development", name: "Custom Software Development" },
    { slug: "saas-development", name: "SaaS Development" },
    { slug: "crm-development", name: "CRM Development" },
    { slug: "mobile-app-development", name: "Mobile App Development" },
    { slug: "web-application-development", name: "Web Application Development" },
    { slug: "edtech-development", name: "EdTech Development" },
    { slug: "mvp-development", name: "MVP Development" },
    { slug: "software-modernisation", name: "Software Modernisation" },
  ];

  return (
    <section className="relative py-16 md:py-24 border-t border-border/60" data-testid="about-trust-band">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-10 lg:gap-14 items-start">
          {/* Left — verified positioning + Discuss Your Project CTA */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-primary">Real engineering, real evidence</div>
            <h2 className="mt-3 font-heading font-bold text-3xl md:text-4xl leading-[1.1] tracking-tight">
              Confidence comes from what we&apos;ve actually shipped.
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-md">
              CloudLab is a working software house — not a marketing surface. Our services are backed by production platforms we
              designed, built and continue to maintain.
            </p>

            <div className="mt-8">
              <CommercialCTA
                variant="discuss-project"
                sourceType="about_trust_band"
                ctaLocation="trust_band"
                testid="about-trust-band-cta"
              />
            </div>
          </div>

          {/* Right — data-driven columns: published case studies + service catalogue */}
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-border bg-card p-6" data-testid="about-trust-band-case-studies">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Published case studies</div>
              <div className="mt-2 font-heading font-black text-3xl leading-none">{caseStudies.length}</div>
              <div className="text-xs text-muted-foreground mt-1">verified engineering write-ups</div>
              <ul className="mt-4 space-y-2.5">
                {caseStudies.map((cs) => (
                  <li key={cs.slug}>
                    <Link
                      to={`/case-studies/${cs.slug}`}
                      className="group flex items-center justify-between gap-3 text-sm text-foreground/85 hover:text-primary transition-colors"
                      data-testid={`about-trust-band-case-${cs.slug}`}
                    >
                      <span className="min-w-0">
                        <span className="font-medium">{cs.projectName}</span>
                        {cs.solutionType && (
                          <span className="text-muted-foreground"> · {cs.solutionType}</span>
                        )}
                      </span>
                      <ArrowUpRight className="w-3.5 h-3.5 shrink-0 opacity-70 group-hover:opacity-100 group-hover:-translate-y-0.5 transition-transform" />
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/case-studies"
                className="mt-5 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-primary hover:underline"
                data-testid="about-trust-band-case-studies-link"
              >
                All case studies <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="rounded-3xl border border-border bg-card p-6" data-testid="about-trust-band-services">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Engineering services</div>
              <div className="mt-2 font-heading font-black text-3xl leading-none">{services.length}</div>
              <div className="text-xs text-muted-foreground mt-1">canonical service capabilities</div>
              <ul className="mt-4 space-y-2">
                {services.slice(0, 6).map((s) => (
                  <li key={s.slug}>
                    <Link
                      to={`/services/${s.slug}`}
                      className="text-sm text-foreground/85 hover:text-primary transition-colors"
                      data-testid={`about-trust-band-service-${s.slug}`}
                    >
                      · {s.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/services"
                className="mt-5 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-primary hover:underline"
                data-testid="about-trust-band-services-link"
              >
                All services <ArrowUpRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Decorative L-shaped corner mark for the team-collage frame
function CornerMark({ className = "" }) {
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none w-6 h-6 md:w-8 md:h-8 ${className}`}
    >
      <span className="absolute top-0 left-0 h-full w-[3px] rounded bg-primary" />
      <span className="absolute top-0 left-0 w-full h-[3px] rounded bg-primary" />
    </span>
  );
}

